"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, MapPin, Search, Sparkles, Target, TrendingUp, Users } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const marketInsights = [{
  icon: Search,
  title: "High-Intent Local Search Volumes",
  stat: "78%",
  statLabel: "Local Search Intent",
  description: "Colchester buyers consistently search with high commercial intent, seeking immediate local services, professional B2B solutions, and retail offerings.",
  highlight: "Over 45,000 monthly high-intent searches targeted within Colchester and surrounding Essex hubs."
}, {
  icon: Target,
  title: "Google Map Pack & Local Organic Gap",
  stat: "3.4x",
  statLabel: "More Conversions",
  description: "Most local Colchester competitors rely on basic directory listings. Capturing top 3 Map Pack rankings delivers immediate qualified phone calls and direct inquiries.",
  highlight: "Firms occupying top organic positions capture 60%+ of total market click-throughs."
}, {
  icon: Users,
  title: "Changing Colchester Consumer Behavior",
  stat: "84%",
  statLabel: "Mobile Search Rate",
  description: "Modern Colchester customers search on mobile while on the go. Fast page loads, structured microdata, and localized entity signals dictate who wins.",
  highlight: "Nearby searches ('SEO consultant near me' or 'B2B services Colchester') converted within 24 hours."
}];
const customerSearchBehaviors = [{
  phase: "Discovery & Intent",
  behavior: "Searching generic + Colchester location modifiers",
  impact: "Captures top-of-funnel buyers actively comparing Essex providers.",
  badge: "High Opportunity"
}, {
  phase: "Map & Entity Validation",
  behavior: "Checking Google Maps, reviews, and localized content authority",
  impact: "Builds immediate credibility over passive competitors.",
  badge: "Trust Trigger"
}, {
  phase: "Direct Consultation",
  behavior: "Seeking direct consultant access rather than opaque agency teams",
  impact: "Higher conversion rates when clients know exactly who delivers their SEO strategy.",
  badge: "Direct Contact"
}];
export default function ColchesterMarket() {
  const [activeTab, setActiveTab] = useState<number>(0);
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="colchestermarketopportunity" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      {/* Background Subtle Gradient Blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-secondary/50 blur-3xl opacity-70" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-accent/10 blur-3xl opacity-60" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="max-w-3xl mx-auto text-center mb-16">
          <motion.div variants={itemVariants} className="inline-block mb-3">
            <Badge variant="secondary" className="px-3.5 py-1.5 text-xs font-semibold tracking-wide rounded-full border border-accent/20 bg-secondary text-foreground">
              <MapPin className="w-3.5 h-3.5 text-primary mr-1" />
              Colchester Market Analysis
            </Badge>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-primary tracking-tight leading-tight">
            Unlocking Colchester’s Local Search Opportunity
          </motion.h2>

          <motion.p variants={itemVariants} className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            When I audit businesses in Colchester and across Essex, I consistently identify huge uncaptured organic market share. Here is how local customer search behavior creates a distinct competitive advantage for businesses working directly with a senior SEO strategy consultant.
          </motion.p>
        </motion.div>

        {/* 3-Column Insights Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {marketInsights.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} variants={itemVariants} data-index={i}>
                <Card className="h-full bg-card border border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 rounded-xl overflow-hidden flex flex-col justify-between">
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center text-primary">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <span className="text-2xl font-bold font-serif text-primary">
                        {item.stat}
                      </span>
                    </div>
                    <Badge variant="outline" className="w-fit text-xs font-medium text-accent-foreground bg-accent/10 border-accent/30 mb-2">
                      {item.statLabel}
                    </Badge>
                    <CardTitle className="text-xl font-bold font-serif text-primary leading-snug">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-6 pt-0 flex-1 flex flex-col justify-between">
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {item.description}
                    </p>

                    {/* Key takeaway */}
                    <div className="p-3.5 rounded-lg bg-secondary/80 border-l-4 border-accent text-xs text-foreground font-medium leading-relaxed">
                      <span className="font-semibold text-primary block mb-0.5">
                        Key Market Insight:
                      </span>
                      {item.highlight}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Journey & Strategy Deep Dive */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-10 shadow-sm mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left overview */}
            <div className="lg:col-span-5">
              <Badge variant="outline" className="mb-3 text-xs font-semibold text-primary border-primary/30">
                <BarChart3 className="w-3.5 h-3.5 mr-1" />
                Search Intent Dynamics
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-primary leading-tight mb-4">
                How Colchester Buyers Make Decisions Online
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                Generic regional SEO tactics often fail because they ignore Colchester’s unique blend of historic commercial heritage, thriving commuter population, and growing digital sector. I structure bespoke strategies that capture demand at every stage of the search journey.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-foreground">
                    Direct hands-on consultant oversight (no junior account managers)
                  </span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-foreground">
                    Hyper-localized keyword mapping for Essex commercial intent
                  </span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-foreground">
                    Technical optimization that guarantees fast, mobile-first indexing
                  </span>
                </div>
              </div>

              {/* Direct access badge */}
              <div className="p-4 rounded-xl bg-secondary/60 border border-accent/20 flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-primary shrink-0" />
                <p className="text-xs text-foreground leading-snug">
                  <strong className="font-semibold text-primary">You work directly with me:</strong> I personally audit, build, and execute your Colchester SEO campaign for maximum ROI.
                </p>
              </div>
            </div>

            {/* Right interactive breakdown list */}
            <div className="lg:col-span-7 space-y-4">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Colchester Customer Journey Breakdown
              </div>

              {customerSearchBehaviors.map((item, i) => <div key={item.phase} data-index={i} onClick={() => setActiveTab(i)} className={`p-5 rounded-xl border transition-all duration-300 cursor-pointer ${activeTab === i ? "bg-background border-primary shadow-xs ring-1 ring-primary/20" : "bg-muted/40 border-border hover:border-primary/40 hover:bg-background"}`}>
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-secondary text-primary font-bold text-xs flex items-center justify-center shrink-0">
                        {i + 1}
                      </span>
                      <h4 className="text-base font-semibold font-serif text-primary">
                        {item.phase}
                      </h4>
                    </div>
                    <Badge variant="secondary" className="text-xs bg-secondary text-foreground font-medium">
                      {item.badge}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium text-foreground pl-10 mb-1">
                    {item.behavior}
                  </p>
                  <p className="text-xs text-muted-foreground pl-10">
                    {item.impact}
                  </p>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Action Callout */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 lg:p-12 text-center relative overflow-hidden shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary to-primary/90" />
          <div className="relative z-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4 bg-secondary text-foreground font-medium text-xs px-3 py-1 rounded-full border-none">
              Ready to Win in Colchester?
            </Badge>

            <h3 className="text-xl sm:text-2xl font-bold font-serif text-primary-foreground tracking-tight mb-4">
              Capitalize on Unclaimed Local Organic Traffic
            </h3>

            <p className="text-sm sm:text-base text-primary-foreground/90 font-sans leading-relaxed mb-8">
              Let us map out your custom Colchester SEO strategy. I will review your technical foundation, competitor positioning, and direct revenue opportunities.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-transform active:scale-95 shadow-md">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/30 text-foreground bg-background hover:bg-secondary font-medium px-6 py-3 rounded-lg">
                <Link to="/CaseStudiesPage">
                  Explore Genuine Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}